// components/Reusablesteps/Healthstepper.jsx
//
// Full flow:
//   WELCOME → CONSENT → NEW_MOBILE → NEW_OTP
//   → STEPS (age/routine/marital/height/weight chips+sliders)
//   → ENTRY_REASON (auto-resolved from age+marital, multi-select, back → last step)
//   → INTAKE (per-reason questionnaire, back → ENTRY_REASON)
//   → TRIAGE → RECOMMENDATION → ACTION
//
// LifeStageScreen REMOVED — life stage auto-resolved from age_group + maritalStatus.

import React, { useState } from "react";
import WelcomeScreen    from "./Welcomescreen";
import StepperProgress  from "./Stepperprogress";
import ChipSelect       from "./Chipselect";
import SummaryScreen    from "./Summaryscreen";
import styles           from "./Healthstepper.module.css";
import { Globalimages } from "../Imagesreusable";

import {
  PatientTypeScreen,
  MobileInputScreen,
  OTPScreen,
  VerifiedScreen,
} from "../onboardingflow";

import ConsentScreen        from "./Consentscreen";
import EntryReasonScreen    from "./Entryreasonscreen";
import IntakeScreen         from "./intake";
// import TriageScreen         from "./Triagescreen";
import RecommendationScreen from "./Recommendationscreen";
import ActionScreen         from "./Actionscreen";
import { useNavigate } from "react-router-dom";

/* ── Screen IDs ─────────────────────────────────────────────────── */
const S = {
  WELCOME:        "WELCOME",
  CONSENT:        "CONSENT",
  NEW_MOBILE:     "NEW_MOBILE",
  NEW_OTP:        "NEW_OTP",
  EX_MOBILE:      "EX_MOBILE",
  EX_OTP:         "EX_OTP",
  EX_VERIFIED:    "EX_VERIFIED",
  STEPS:          "STEPS",
  ENTRY_REASON:   "ENTRY_REASON",
  INTAKE:         "INTAKE",
  TRIAGE:         "TRIAGE",
  RECOMMENDATION: "RECOMMENDATION",
  ACTION:         "ACTION",
};

const HealthStepper = ({
  welcome       = {},
  questions     = [],
  onComplete,
  onSkip,
  confirmLink,
  onWelcomeNext,
}) => {

  const [screen,       setScreen]       = useState(S.WELCOME);
  const [name,         setName]         = useState("");
  const [phone,        setPhone]        = useState("");
  const [current,      setCurrent]      = useState(0);
  const [answers,      setAnswers]      = useState({});
  const [textVal,      setTextVal]      = useState({});
  const [sliderVal,    setSliderVal]    = useState({});
  const [showSummary,  setShowSummary]  = useState(false);
  const [entryPayload, setEntryPayload] = useState(null);
  const [intakeAnswers,setIntakeAnswers]= useState(null);
  const [triageResult, setTriageResult] = useState(null);
  const [chosenAction, setChosenAction] = useState("");

  const total = questions.length;
  const step  = questions[current];

  const EX_TOTAL  = 3;
  const NEW_TOTAL = 2;
  const exStep  = screen === S.EX_MOBILE ? 0 : screen === S.EX_OTP ? 1 : 2;
  const newStep = screen === S.NEW_MOBILE ? 0 : 1;

  /* ── LocalStorage ─────────────────────────────────────────────── */
  const saveToLocalStorage = (data) => {
    try {
      const pp = {
        name:         data.name || data.contact_name || data.questions_name || "",
        mobile:       (data.mobile || data.contact_phone || data.questions_phone || "").toString().slice(0, 10),
        age:          data.age ?? data.ageGroup ?? data.age_group ?? "",
        age_group:    data.age_group ?? data.ageGroup ?? data.age ?? "",   // ← resolver reads this
        heightCm:     data.heightCm !== undefined && data.heightCm !== "" ? Number(data.heightCm)
                      : data.height !== undefined && data.height !== ""   ? Number(data.height)   : "",
        weightKg:     data.weightKg !== undefined && data.weightKg !== "" ? Number(data.weightKg)
                      : data.weight !== undefined && data.weight !== ""   ? Number(data.weight)   : "",
        profession:   data.profession || data.routine || data.daily_routine || "",
        maritalStatus:data.maritalStatus || data.marital_status || data.marital || "",
      };
      const existing  = JSON.parse(localStorage.getItem("patientPersona") || "{}");
      const finalData = {};
      for (const key of Object.keys({ ...existing, ...pp })) {
        finalData[key] = pp[key] !== "" && pp[key] !== undefined ? pp[key] : existing[key] ?? "";
      }
      finalData.savedAt = Date.now();
      localStorage.setItem("patientPersona", JSON.stringify(finalData));
    } catch (err) { console.error("❌ LocalStorage Error:", err); }
  };

  const patientData = (() => {
    try { return JSON.parse(localStorage.getItem("patientPersona") || "{}"); }
    catch { return {}; }
  })();

  /* ── Handlers ─────────────────────────────────────────────────── */
  const handleExistingVerify   = () => { saveToLocalStorage({ mobile: phone }); setScreen(S.EX_VERIFIED); };
  const handleExistingComplete = () => { onComplete?.({ formAnswers: { phone } }); window.location.href = confirmLink; };
  const handleNewVerify        = () => { saveToLocalStorage({ name, mobile: phone }); setCurrent(0); setScreen(S.STEPS); };
 const navigate = useNavigate();
  const advance = () => {
    const sliderDefaults = {};
    questions.forEach((q) => {
      if (q.type === "slider" && sliderVal[q.id] === undefined)
        sliderDefaults[q.id] = q.sliderDefault ?? q.sliderMin ?? 0;
    });
    const finalData = { ...answers, ...sliderDefaults, ...sliderVal, ...textVal };
    saveToLocalStorage(finalData);
   if (current < total - 1) {
      setCurrent((p) => p + 1);
    } else {
      navigate("/symptoms");   // ← soft navigation, preserves React state tree
    }
  };

  const handleEntryReasonBack = () => { setCurrent(total - 1); setScreen(S.STEPS); };

  const handleEntryReasonNext = (payload) => {
    setEntryPayload(payload);
    try {
      const existing = JSON.parse(localStorage.getItem("patientPersona") || "{}");
      localStorage.setItem("patientPersona", JSON.stringify({
        ...existing,
        entryReasons: payload.reasons,
        entryRoutes:  payload.routes,
        journeyTitle: payload.journeyTitle,
        lifeStageKey: payload.lifeStageKey,
      }));
    } catch (e) { /* ignore */ }
    setScreen(S.INTAKE);
  };

  const handleChip   = (id, val) => setAnswers(p   => ({ ...p, [id]: val }));
  const handleSlider = (id, val) => setSliderVal(p  => ({ ...p, [id]: val }));
  const handleText   = (id, val) => setTextVal(p    => ({ ...p, [id]: val }));

  const pct = (s) => {
    const val = sliderVal[s.id] ?? s.sliderDefault ?? s.sliderMin ?? 0;
    return ((val - s.sliderMin) / (s.sliderMax - s.sliderMin)) * 100;
  };

  const buildSummaryItems = () => {
    const all    = { ...answers, ...sliderVal, ...textVal };
    const AGE_IDS = new Set(["age", "ageGroup", "age_group"]);
    return questions
      .filter((q) => {
        if (q.type === "contact") return false;
        if (AGE_IDS.has(q.id)) return false;
        if (q.type === "slider") return all[q.id] !== undefined || q.sliderDefault !== undefined;
        return all[q.id] !== undefined;
      })
      .map((q) => {
        let value;
        if (q.type === "slider") {
          const v = all[q.id] ?? q.sliderDefault ?? q.sliderMin ?? 0;
          if (q.id === "heightCm") {
            const ft = Math.floor(v / 12), inch = v % 12;
            value = inch === 0 ? `${ft}.0 Ft.` : `${ft}.${inch} Ft.`;
          } else {
            value = `${Number(v).toFixed(q.sliderDecimals ?? 0)} ${q.sliderUnit || ""}`;
          }
        } else { value = all[q.id]; }
        return { icon: q.summaryIcon || "", label: q.summaryLabel || q.question, value };
      });
  };

  /* ── Step body renderer ───────────────────────────────────────── */
  const renderBody = () => {
    if (!step) return null;

    if (step.type === "chips") {
      return (
        <ChipSelect
          showIcons={true}
          options={step.options}
          value={answers[step.id]}
          onChange={val => handleChip(step.id, val)}
          onAdvance={advance}
        />
      );
    }

    if (step.type === "slider") {
      const val      = sliderVal[step.id] ?? step.sliderDefault ?? step.sliderMin ?? 0;
      const dec      = step.sliderDecimals ?? 0;
      const isHeight = step.id === "heightCm";
      const fmtH     = (ti) => { const ft = Math.floor(ti / 12), inch = ti % 12; return inch === 0 ? `${ft}.0 Ft.` : `${ft}.${inch} Ft.`; };
      const display  = isHeight ? fmtH(val) : `${Number(val).toFixed(dec)} ${step.sliderUnit || ""}`;
      return (
        <div className={styles.sliderSection}>
          <div className={styles.sliderTopRow}>
            <span className={styles.sliderLabel}>{step.sliderLabel || step.question}</span>
            <span className={styles.sliderValue}>{display}</span>
          </div>
          <div className={styles.trackWrap}>
            <div className={styles.trackFill} style={{ width: `${pct(step)}%` }} />
            <input type="range" className={styles.range}
              min={step.sliderMin} max={step.sliderMax} step={step.sliderStep ?? 1} value={val}
              onChange={(e) => handleSlider(step.id, parseFloat(e.target.value))}
            />
          </div>
          {step.sliderTicks?.length > 0 && (
            <div className={styles.ticks}>
              {step.sliderTicks.map((t) => <span key={t} className={styles.tick}>{t}</span>)}
            </div>
          )}
          <button className={styles.saveBtn} onClick={advance} type="button">
            {current === total - 1 ? "Next" : "Save"}
          </button>
        </div>
      );
    }

    if (step.type === "text" || step.type === "number") {
      return (
        <div className={styles.textSection}>
          <input type={step.type === "number" ? "tel" : "text"}
            className={`ava-text-body ${styles.textInput}`}
            placeholder={step.placeholder || ""}
            value={textVal[step.id] ?? ""}
            maxLength={step.type === "number" ? 10 : undefined}
            onChange={e => handleText(step.id, e.target.value)}
          />
        </div>
      );
    }

    if (step.type === "contact") {
      return (
        <div className={styles.contactSection}>
          <input type="text" className={styles.textInput} placeholder="Enter your name"
            value={textVal[`${step.id}_name`] ?? ""}
            onChange={e => handleText(`${step.id}_name`, e.target.value)}
          />
          <div className={styles.phoneWrapper}>
            <span className={styles.countryCode}>+91</span>
            <input type="tel" className={styles.textInput} placeholder="10 digit mobile number" maxLength={10}
              value={textVal[`${step.id}_phone`] ?? ""}
              onChange={e => handleText(`${step.id}_phone`, e.target.value.replace(/\D/g, ""))}
            />
          </div>
          <button className={styles.summaryBtn} type="button"
            onClick={() => { saveToLocalStorage({ ...answers, ...textVal, ...sliderVal }); setShowSummary(true); }}>
            Show summary
          </button>
        </div>
      );
    }
    return null;
  };

  /* ── Card Shell ───────────────────────────────────────────────── */
  const CardShell = ({ children }) => (
    <div className={styles.card}>
      <div className={styles.headerBar}>
        <img src={Globalimages.logo} alt="Avexa Logo" className={styles.logo} />
      </div>
      {children}
      <div className={styles.secureInfo}>
        <img src={Globalimages.image13} alt="" className={styles.secureInfoicon} />
        <span className="ava-text-caption">Your information is private and secure</span>
      </div>
    </div>
  );

  const isChips   = step?.type === "chips";
  const areaClass = isChips ? styles.centreArea : styles.topArea;

  /* ── Render Switch ────────────────────────────────────────────── */

  if (screen === S.WELCOME) return (
    <div className={styles.welcomecard}>
      <div className={styles.headerBar}>
        <a href="/"><img src={Globalimages.logo} alt="Avexa Logo" className={styles.logo} /></a>
      </div>
      <div className={styles.welcomecard}>
        <WelcomeScreen
          title={welcome.title} subtitle={welcome.subtitle}
          buttonText={welcome.buttonText} skipLabel={welcome.skipLabel} skipLink={welcome.skipLink}
          onStart={() => onWelcomeNext ? onWelcomeNext() : setScreen(S.CONSENT)}
          onSkip={onSkip}
        />
      </div>
    </div>
  );

  if (screen === S.CONSENT) return (
    <CardShell>
      <ConsentScreen onAgree={() => setScreen(S.NEW_MOBILE)} onDecline={onSkip} />
    </CardShell>
  );

  if (screen === S.NEW_MOBILE) return (
    <CardShell>
      <MobileInputScreen isNew={true} initialName={name} initialPhone={phone}
        onNext={(nm, ph) => { setName(nm); setPhone(ph); setScreen(S.NEW_OTP); }}
        currentStep={newStep} totalSteps={NEW_TOTAL}
      />
    </CardShell>
  );

  if (screen === S.NEW_OTP) return (
    <CardShell>
      <OTPScreen phone={phone} onVerify={handleNewVerify} currentStep={newStep} totalSteps={NEW_TOTAL} />
    </CardShell>
  );

  if (screen === S.EX_MOBILE) return (
    <CardShell>
      <MobileInputScreen isNew={false} initialPhone={phone}
        onNext={(_, ph) => { setPhone(ph); setScreen(S.EX_OTP); }}
        currentStep={exStep} totalSteps={EX_TOTAL}
      />
    </CardShell>
  );

  if (screen === S.EX_OTP) return (
    <CardShell>
      <OTPScreen phone={phone} onVerify={handleExistingVerify} currentStep={exStep} totalSteps={EX_TOTAL} />
    </CardShell>
  );

  if (screen === S.EX_VERIFIED) return (
    <CardShell>
      <VerifiedScreen name={name} ctaLabel="Go To Health Assessment" onContinue={handleExistingComplete} />
    </CardShell>
  );

  if (showSummary) return (
    <div className={styles.card}>
      <SummaryScreen
        confirmLink={confirmLink} patient={patientData} items={buildSummaryItems()}
        onEdit={() => setShowSummary(false)}
        onConfirm={() => onComplete?.({ formAnswers: { ...answers, ...sliderVal, ...textVal } })}
        heightCm={sliderVal.heightCm ?? answers.heightCm ?? patientData.heightCm}
        weightKg={sliderVal.weightKg ?? answers.weightKg ?? patientData.weightKg}
      />
    </div>
  );

  /* ── Entry Reason — multi-select, auto life stage ── */
  if (screen === S.ENTRY_REASON) return (
    <CardShell>
      <EntryReasonScreen
        onNext={handleEntryReasonNext}
        onBack={handleEntryReasonBack}
      />
    </CardShell>
  );

  /* ── Intake questionnaire (one per reason, back → EntryReason) ── */
  if (screen === S.INTAKE) return (
    <CardShell>
      <IntakeScreen
        entryPayload={entryPayload}
        patientData={patientData}
        onComplete={(allAns) => { setIntakeAnswers(allAns); setScreen(S.RECOMMENDATION); }}
        onBack={() => setScreen(S.ENTRY_REASON)}
      />
    </CardShell>
  );

  // if (screen === S.TRIAGE) return (
  //   <CardShell>
  //     <TriageScreen
  //       entryReason={entryPayload}
  //       patientData={patientData}
  //       onComplete={(result) => { setTriageResult(result); setScreen(S.RECOMMENDATION); }}
  //     />
  //   </CardShell>
  // );

  // if (screen === S.RECOMMENDATION) return (
  //   <CardShell>
  //     <RecommendationScreen
  //       riskTier={triageResult?.riskTier || "Low"}
  //       entryReason={entryPayload}
  //       patientData={patientData}
  //       onSelect={(action) => { setChosenAction(action); setScreen(S.ACTION); }}
  //     />
  //   </CardShell>
  // );

  // if (screen === S.ACTION) return (
  //   <CardShell>
  //     <ActionScreen
  //       action={chosenAction}
  //       riskTier={triageResult?.riskTier || "Low"}
  //       patientData={patientData}
  //       onBook={()   => { onComplete?.({ action: "book",   patientData, triageResult, intakeAnswers }); window.location.href = confirmLink; }}
  //       onAssess={()  => { onComplete?.({ action: "assess",  patientData, triageResult, intakeAnswers }); window.location.href = confirmLink; }}
  //       onEnroll={()  => { onComplete?.({ action: "enroll",  patientData, triageResult, intakeAnswers }); window.location.href = confirmLink; }}
  //     />
  //   </CardShell>
  // );

  /* ── Basic-info step questions ── */
  return (
    <div className={styles.card}>
      <div className={styles.headerBar}>
        <img src={Globalimages.logo} alt="Avexa Logo" className={styles.logo} />
      </div>
      <div className={styles.stepWrapper}>
        <div className={styles.progressArea}>
          <StepperProgress current={current} total={total} />
        </div>
        <div className={areaClass}>
          <h2 className={`ava-heading-md ${styles.question}`}>
            {step?.question}<br />{step?.text || ""}
          </h2>
          {step?.summarytext && <p className={`ava-text-caption ${styles.summarytext}`}>{step.summarytext}</p>}
          {step?.description && <p className={`ava-text-caption ${styles.descriptionText}`}>{step.description}</p>}
          <div className={styles.answerArea}>{renderBody()}</div>
        </div>
        {step?.info && (
          <div className={styles.secureInfo}>
            <img src={Globalimages.image13} alt="" className={styles.secureInfoicon} />
            <span className="ava-text-caption">Your information is private and secure</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default HealthStepper;